/**
 * Lightyear has no personal API, so data comes from the in-app export:
 * Profile → Reports → Transaction report (CSV).
 * Expected columns (case-insensitive): Date, Type, Ticker, ISIN, Quantity, Price, Currency, Total, Fee, FX Rate
 */

export function parseCSV(text) {
  const rows = []
  let row = [], field = '', quoted = false
  const delim = detectDelimiter(text)
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++ }
      else if (c === '"') quoted = false
      else field += c
    } else if (c === '"') quoted = true
    else if (c === delim) { row.push(field); field = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++
      row.push(field); field = ''
      if (row.some((f) => f.trim() !== '')) rows.push(row)
      row = []
    } else field += c
  }
  row.push(field)
  if (row.some((f) => f.trim() !== '')) rows.push(row)
  return rows
}

function detectDelimiter(text) {
  const first = text.split(/\r?\n/, 1)[0]
  return (first.match(/;/g) || []).length > (first.match(/,/g) || []).length ? ';' : ','
}

const num = (v) => {
  if (v == null || String(v).trim() === '') return 0
  const n = Number(String(v).replace(/\s/g, '').replace(/,(?=\d{1,2}$)/, '.').replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}

const ALIASES = {
  date: ['date', 'trade date', 'datetime'],
  type: ['type', 'transaction type'],
  ticker: ['ticker', 'symbol'],
  isin: ['isin'],
  quantity: ['quantity', 'qty', 'shares'],
  price: ['price', 'price/share'],
  currency: ['currency', 'ccy'],
  total: ['total', 'net amt.', 'net amount', 'amount'],
  fee: ['fee', 'fees'],
  fx: ['fx rate', 'fx'],
}

export function parseLightyear(text) {
  const rows = parseCSV(text.replace(/^﻿/, ''))
  if (rows.length < 2) throw new Error('The CSV has no data rows.')
  const header = rows[0].map((h) => h.trim().toLowerCase())
  const col = {}
  for (const [key, names] of Object.entries(ALIASES)) col[key] = header.findIndex((h) => names.includes(h))
  const missing = ['date', 'type', 'total'].filter((k) => col[k] < 0)
  if (missing.length) throw new Error(`Missing column(s): ${missing.join(', ')}. Is this a Lightyear transaction report?`)
  const get = (r, k) => (col[k] >= 0 ? (r[col[k]] ?? '').trim() : '')
  return rows.slice(1).map((r) => ({
    date: get(r, 'date').slice(0, 10),
    type: get(r, 'type').toUpperCase(),
    ticker: get(r, 'ticker'),
    isin: get(r, 'isin'),
    quantity: num(get(r, 'quantity')),
    price: num(get(r, 'price')),
    currency: get(r, 'currency') || 'EUR',
    total: num(get(r, 'total')),
    fee: num(get(r, 'fee')),
  })).filter((t) => t.date && t.type)
}

/** Aggregate transactions into holdings (average cost) and deposit totals per currency. */
export function summarizeLightyear(transactions) {
  const holdings = {}
  const deposits = {}
  const dividends = {}
  for (const t of transactions) {
    const key = t.isin || t.ticker
    const abs = Math.abs(t.total)
    if (t.type === 'BUY' && key) {
      const h = (holdings[key] ||= { ticker: t.ticker, isin: t.isin, currency: t.currency, quantity: 0, cost: 0 })
      h.quantity += Math.abs(t.quantity)
      h.cost += abs + Math.abs(t.fee)
    } else if (t.type === 'SELL' && key && holdings[key]) {
      const h = holdings[key]
      const q = Math.min(Math.abs(t.quantity), h.quantity)
      const avg = h.quantity ? h.cost / h.quantity : 0
      h.quantity -= q
      h.cost -= avg * q
    } else if (t.type === 'DEPOSIT') deposits[t.currency] = (deposits[t.currency] || 0) + abs
    else if (t.type === 'WITHDRAWAL') deposits[t.currency] = (deposits[t.currency] || 0) - abs
    else if (t.type === 'DIVIDEND' || t.type === 'TAX' || t.type === 'INTEREST') {
      dividends[t.currency] = (dividends[t.currency] || 0) + t.total
    }
  }
  const list = Object.values(holdings)
    .filter((h) => h.quantity > 1e-9)
    .map((h) => ({ ...h, avgCost: h.cost / h.quantity }))
    .sort((a, b) => b.cost - a.cost)
  return { holdings: list, deposits, dividends }
}
